import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, SelectChangeEvent, Typography } from '@mui/material'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useLocation, useNavigate } from 'react-router-dom'
import theme from 'theme'
import { useThemeColors } from 'hooks/useThemeColors'

/* Utils */
import axios from '../../../lib/axios'
import { breadcrumbs, groupInsights } from 'lib/utils'
import { getInsightsV2Enabled, INSIGHTS_V2_BASE, INSIGHTS_V3_BASE } from 'lib/insightsApi'
import { CPITimePeriod, Insight, InsightSortBy, InsightSubType } from 'models'
import { LabelOption } from '../library/components/LibrarySelectLabelField'
import useDebounce from '../../../hooks/useDebounce'
import { useSearchInsights, DataSourceOption } from '../../../hooks/useSearchInsights'

/* Components */
import { Container, Content, NoResultsWrapper } from './onyxAIInsights.styles'
import MainSection from './sections/main-section'
import InsightErrorBoundary, { InsightErrorFallback } from './components/InsightErrorBoundary'
import useSearchLabel from '../../../hooks/useSearchLabel'
import ContentWrapper from '../../components/header/ContentWrapper'
import InsightsList from './sections/insights-list'
import InsightsCustomHeader from './components/InsightsCustomHeader'
import FiltersToolbar from './components/FiltersToolbar'
import NoIntegrations from './sections/NoIntegrations'
import { resolveNoIntegrationsVersion } from './insightsEmptyState'
import { MonthRangePicker } from '@khan_skadi/month-range-picker'
import { ViewData, viewDataStyles } from './components/insightsTimePicker.styles'
import ThemeButton from '../../components/buttons/primary/ThemeButton'
import { formatCustomTimePickerValue, monthDiff } from '../library/utils'

dayjs.extend(isBetween)

const getDaysIntMonth = (y: number, m: number) => new Date(y, m, 0).getDate()

type RangeParams = {
  startMonth: number
  startYear: number
  endMonth: number
  endYear: number
}

const headerBackgroundVariants = {
  green: '/insights/green-insights-bg.png',
  red: '/insights/red-insights-bg.png'
}

export const missingCoverageTypes = [
  InsightSubType.MissingAssetTypeCoverage,
  InsightSubType.MissingCategoryCoverage,
  InsightSubType.MissingStrategicCoverage
]

export interface Filters {
  time: string
  subType: string[]
  cpis: string[]
  dataSources: string[]
  severity: string[]
  sortBy: InsightSortBy
  sortDirection: 'ASC' | 'DESC'
}

const defaultFilters: Filters = {
  time: 'All Time',
  subType: [],
  cpis: [],
  dataSources: [],
  severity: [],
  sortBy: InsightSortBy.CreatedAt,
  sortDirection: 'DESC'
}

const severityOptions = [
  { name: 'All', label: 'All Severities' },
  { name: 'Critical', label: 'Critical Severities' },
  { name: 'High', label: 'High Severities' },
  {
    name: 'Medium',
    label: 'Medium Severities'
  },
  { name: 'Low', label: 'Low Severities' }
]

const timeOptions: LabelOption[] = [
  { name: 'All Time', label: 'All Time' },
  { name: 'Today', label: 'Today' },
  { name: 'Last Month', label: 'Last Month' },
  // { name: 'From Last Session', label: 'From Last Session' },
  { name: 'Custom...', label: 'Custom...' }
]

const typeOptions: LabelOption[] = [
  {
    name: InsightSubType.Improvement,
    label: 'Data Trend Improvement'
  },
  {
    name: InsightSubType.CrossedIntoGreen,
    label: 'Data Trending into Green Zone'
  },
  {
    name: InsightSubType.Reduction,
    label: 'Data Trend Reduction'
  },
  {
    name: InsightSubType.CrossedIntoRed,
    label: 'Data Trending into Red Zone'
  },
  {
    name: InsightSubType.MissingCoverage,
    label: 'Missing Coverage'
  },
  {
    name: InsightSubType.CpiSlaOptimization,
    label: 'SLA Optimization'
  }
]

const OnyxAIInsights: FC = () => {
  const getColor = useThemeColors()
  const location = useLocation()
  const navigate = useNavigate()
  const [initialLoad, setInitialLoad] = useState(false)
  const [results] = useState<Insight[]>([])
  const [filtersToolbarOpen, setFiltersToolbarOpen] = useState(false)
  // True only on the first render after arriving via Category-page navigation
  // and only while the user hasn't interacted yet. Used to suppress the filter
  // toolbar when the category has no insights — the user lands on a clean
  // empty state instead of an empty list with stale filter chips.
  const [cameFromCategoryNav, setCameFromCategoryNav] = useState(false)
  // Locks the cpiOptions populated from category navigation so the data-driven
  // effect below can't silently replace them with options derived from the
  // (potentially unfiltered) insight results — that's what caused the chip to
  // show "2 selected" while the dropdown listed unrelated CPIs.
  const categoryNavCpiOptionsRef = useRef<LabelOption[] | null>(null)
  const [cpiOptions, setCpiOptions] = useState<LabelOption[]>([])
  const [configuredDataSources, setConfiguredDataSources] = useState<DataSourceOption[]>([])
  // Every source the tenant has configured, rather than only those the first page of results
  // happened to mention: deriving this from search results left the filter empty whenever that
  // page was thin, and the empty list then stayed locked in for the rest of the session.
  const dataSourceOptions = configuredDataSources
  const [filters, setFilters] = useState<Filters>(defaultFilters)
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null)
  const filtersActive = useMemo(() => {
    return (
      (filters.time.length > 0 && filters.time !== 'All Time') ||
      filters.subType.length > 0 ||
      filters.cpis.length > 0 ||
      filters.dataSources.length > 0 ||
      filters.severity.length > 0
    )
  }, [filters])
  const convertedOptions = useMemo(
    () =>
      results.map((item) => ({
        label: item.data?.cpi_display_name || '',
        name: item.data?.cpi_name || ''
      })),
    []
  )
  const [customTimePeriodValue, setCustomTimePeriodValue] = useState<string>('')
  // v2-insights rollout gate: null = not yet resolved. Route base path off it.
  const [insightsV2, setInsightsV2] = useState<boolean | null>(null)
  const insightsBase = insightsV2 ? INSIGHTS_V2_BASE : INSIGHTS_V3_BASE
  useEffect(() => {
    getInsightsV2Enabled().then(setInsightsV2)
  }, [])
  const checkReadStatus = async (integrationIds: string[]) => {
    const payload = {
      ids: integrationIds
    }

    try {
      const res = await axios.post(`${insightsBase}/check-read-status`, payload)

      if (res.status.toString().includes('20')) {
        return res.data.data
      }
    } catch (err) {
      console.error(err)
    }
  }
  const changeReadStatus = async (integrationIds: string[], read: boolean) => {
    // Don't post a read/unread toggle until the gate resolves — cached list data could
    // otherwise route a click to v3 on a v2 tenant.
    if (insightsV2 === null) return
    const payload = {
      ids: integrationIds
    }

    // Patch the cached list rather than refetching it. A read toggle changes one flag, but a
    // refetch empties `data` and drops the whole page into its loading branch.
    const rollback = applyReadStatus(integrationIds, read)

    try {
      await axios.post(`${insightsBase}/${read ? '' : 'un'}read`, payload)
    } catch (err) {
      console.error(err)
      rollback()
    }
  }
  const { search, setSearch } = useSearchLabel(convertedOptions, null, true)
  const debouncedSearchValue = useDebounce(search, 350)
  const [lastChanged, setLastChanged] = useState('')
  const { data, loading, initialLoading, applyReadStatus } = useSearchInsights({
    value: debouncedSearchValue,
    filters,
    reset: false,
    dataSourceOptions,
    initialLoad,
    lastChanged,
    changeReadStatus,
    checkReadStatus,
    customTimePeriodValue,
    cpiOptions,
    staleTime: 1000 * 60,
    keepPreviousData: false,
    enabled: initialLoad && insightsV2 !== null,
    basePath: insightsBase
  })
  const [loadingMarking, setLoadingMarking] = useState(false)
  const [markedInitialRead, setMarkedInitialRead] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [dataSourcesLoaded, setDataSourcesLoaded] = useState(false)
  const allCardsAreInsights = useMemo(
    () =>
      (data && data.every((c: Insight) => c.type === 'ssm_insight')) ||
      (filters.subType.length > 0 &&
        filters.subType.every(
          (subType) =>
            subType === InsightSubType.MissingAssetTypeCoverage ||
            subType === InsightSubType.MissingCategoryCoverage ||
            subType === InsightSubType.MissingStrategicCoverage
        )),
    [data, filters]
  )

  useEffect(() => {
    const state = (location as any)?.state as {
      types?: string[]
      cpis?: string[] | { name: string; label: string }[]
      time?: string
      categoryScoped?: boolean
    } | null
    if (location && state && (state.types || state.cpis || state.categoryScoped)) {
      // When coming from the Category page the nav state carries rich CPI
      // options ({name, label}) so the dropdown can scope to the category
      // before any insights data has loaded. Legacy callers still pass plain
      // string labels — handle both shapes and derive the labels used in
      // `filters.cpis`.
      const cpiEntries = (state.cpis ?? []) as Array<string | { name: string; label: string }>
      const cpiOptionsFromState = cpiEntries
        .map((c) => (typeof c === 'string' ? { name: c, label: c } : c))
        .filter((c) => c.name && c.label)
      const cpiLabels = cpiOptionsFromState.map((c) => c.label)

      setFilters((pre) => ({
        ...pre,
        ...(state.types && state.types.length > 0 ? { subType: state.types } : {}),
        // Apply the scoped CPI list even when it's empty — that's how a
        // category with no active CPIs is recognised. Without this the page
        // falls back to an unfiltered search and shows insights from other
        // categories.
        ...(state.categoryScoped || cpiLabels.length > 0 ? { cpis: cpiLabels } : {}),
        time: state.time ?? 'Last Month'
      }))

      // Pre-populate cpiOptions so useSearchInsights can resolve the
      // label→name mapping on the first request. Without this the API call
      // races the data-driven cpiOptions initialization and the CPI filter
      // is silently dropped.
      if (cpiOptionsFromState.length > 0) {
        const sorted = cpiOptionsFromState.slice().sort((a, b) => (a.label > b.label ? 1 : -1))
        setCpiOptions(sorted)
        // Lock these options so the data-derived effect never replaces them.
        categoryNavCpiOptionsRef.current = sorted
      }

      if (state.categoryScoped || cpiOptionsFromState.length > 0) {
        setCameFromCategoryNav(true)
      }

      // Open the filters bar automatically so the user lands on a screen that
      // visually reflects the scoping that was carried over from the source.
      setFiltersToolbarOpen(true)

      // Replace the current history entry so the back button returns to the
      // source page (e.g. category dashboard) on the first click instead of
      // landing on /insights again with the original location.state.
      navigate('/insights', { replace: true })
    }
  }, [location])

  const getGroups = (data: Insight[]): Insight[][] => {
    if (data && data.length > 0) {
      return groupInsights(data, filters.sortBy, filters.sortDirection)
    }

    return []
  }

  const groupData = (d: Insight[]): Insight[][] => {
    if (d && d.length > 0) {
      return getGroups(d)
    }

    return []
  }

  const [groups, setGroups] = useState<Insight[][]>([])

  useEffect(() => {
    if (data) {
      const groupedData = groupData(data)
      setGroups(groupedData)

      if (!cpiOptions.length && !categoryNavCpiOptionsRef.current) {
        const displayNames = data.map((n: any) => n.data.cpi_display_name)
        const cpiList: LabelOption[] = []
        data.forEach((c: any) => {
          if (c.type === 'ssm_insight') return
          if (displayNames.includes(c.data.cpi_display_name)) {
            const found = cpiList.find((n) => n.label === c.data.cpi_display_name)

            if (!found) {
              cpiList.push({ name: c.data.cpi_name, label: c.data.cpi_display_name })
            }
          }
        })
        setCpiOptions(cpiList.sort((a, b) => (a.label > b.label ? 1 : -1)))
      }
    } else {
      const haveGroups = groups.flat()
      if (!loading && haveGroups.length > 0) {
        setGroups([])
      }
    }

    return () => {
      // setCpiOptions([])
      setGroups([])
    }
  }, [data, dataSourceOptions, configuredDataSources])

  /* A tenant with no insights never resolves the sources fetch into a list, so stop waiting on one. */
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!data) {
        setDataSourcesLoaded(true)
      }
    }, 3000)

    if (data) clearTimeout(timeout)

    return () => clearTimeout(timeout)
  }, [data])

  const getConfiguredIntegrations = async () => {
    try {
      const res = await axios.get('/api/v3/integrations/configured')

      if (res.status.toString().includes('20')) {
        const instances = ((res.data.instances ?? []) as any[]).filter((n) => !n.deleted)
        // One option per integration, but carrying every instance id behind it: a tenant can run
        // several instances of the same product, and picking the vendor has to match all of them.
        const byIntegration = new Map<string, DataSourceOption>()
        instances.forEach((n) => {
          const existing = byIntegration.get(n.integration_name)
          if (existing) {
            if (!existing.ids?.includes(n.id)) existing.ids?.push(n.id)
            return
          }
          byIntegration.set(n.integration_name, {
            integration_name: n.integration_name,
            name: n.name,
            id: n.id,
            ids: [n.id],
            label: n.integration_name
          })
        })

        setConfiguredDataSources(Array.from(byIntegration.values()))
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (!initialLoad) {
      getConfiguredIntegrations().finally(() => {
        setDataSourcesLoaded(true)
        setInitialLoad(true)
      })
    }
  }, [initialLoad])

  useEffect(() => {
    if (groups && groups.flat().length > 0 && !selectedInsight) {
      setSelectedInsight(groups.flat()[0])
    }
  }, [groups, filters, selectedInsight])

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        setIsLoading(false)
      }, 50)
    }
  }, [loading])

  useEffect(() => {
    const g = groups.flat()
    if (g.length > 0 && !!selectedInsight) {
      const found = g.find((c) => c.id === selectedInsight.id)

      if (!found) {
        setSelectedInsight(g[0])
      }
    }
  }, [groups, debouncedSearchValue])

  useEffect(() => {
    if (data && selectedInsight && !selectedInsight.read && !markedInitialRead) {
      const thisInsight = (data as Insight[]).find((c) => c.id === selectedInsight.id)

      const timeout = setTimeout(() => {
        if (thisInsight && selectedInsight.id === thisInsight.id) {
          setSelectedInsight({ ...selectedInsight, read: true })
          changeReadStatus([selectedInsight.id], true)
          setMarkedInitialRead(true)
        }
      }, 5000)

      return () => clearTimeout(timeout)
    }
  }, [data, selectedInsight, markedInitialRead])

  const handleChangeFilters = async (newValue: any, field: string) => {
    setIsLoading(true)
    // The open insight stays selected while the new result set loads; it's swapped for the
    // first card only if it turns out to be filtered away. Clearing it here unmounted the
    // whole split view for the length of the request.
    setCameFromCategoryNav(false)
    categoryNavCpiOptionsRef.current = null
    const temp = { ...filters } as any
    temp[field] = newValue
    setFilters(temp)
    setLastChanged(`${Math.random()}`)
  }

  const getHeaderBackground = (text: string) => {
    if (!text) return headerBackgroundVariants.red
    if (text.includes('red')) return headerBackgroundVariants.red
    if (text.includes('green')) return headerBackgroundVariants.green
    if (text.includes('improvement')) return headerBackgroundVariants.green

    return headerBackgroundVariants.red
  }

  const handleResetFilters = () => {
    setCameFromCategoryNav(false)
    categoryNavCpiOptionsRef.current = null
    // Clear cpiOptions so the data-driven effect re-derives the full list
    // from the now-unfiltered insights response. Without this, the dropdown
    // stays scoped to whatever CPIs were locked on entry (e.g. category nav
    // pinned 2 CPIs) even though the result set now contains many more.
    setCpiOptions([])
    setFilters((pre) => ({
      ...pre,
      time: 'All Time',
      subType: [],
      cpis: [],
      dataSources: [],
      severity: []
    }))
    setCustomTimePeriodValue('')
  }

  // Per-dropdown reset for the CPI filter. Hoisted to the parent so it can
  // also drop the category-nav lock and clear cpiOptions — otherwise the
  // dropdown stays scoped to the original 2 CPIs even though the unfiltered
  // result set now contains many more.
  const handleResetCpisFilter = () => {
    setCameFromCategoryNav(false)
    categoryNavCpiOptionsRef.current = null
    setCpiOptions([])
    setFilters((pre) => ({ ...pre, cpis: [] }))
  }

  // Selection is local state and the read flag is patched straight into the cache, so this
  // stays synchronous — awaiting the request here is what made a click feel like a reload.
  const handleClickInsight = (insightId: string) => {
    const foundInsight = (data as Insight[] | undefined)?.find((c) => c.id === insightId)
    if (!foundInsight) return

    setSelectedInsight({ ...foundInsight, read: true })

    if (!foundInsight.read) {
      changeReadStatus([foundInsight.id], true)
    }
  }

  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [viewDataDisabled, setViewDataDisabled] = useState(true)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const handleCustomClick = () => {
    setPickerOpen(!pickerOpen)
  }

  const closeCustomPicker = () => {
    setPickerOpen(false)
  }

  const onRangeSelect = (params: RangeParams) => {
    const { startMonth, endMonth, startYear, endYear } = params
    const stringMonthStart = startMonth.toString().padStart(2, '0')
    const stringMonthEnd = endMonth.toString().padStart(2, '0')
    if (startMonth && startYear) {
      setStart(`${startYear}-${stringMonthStart}-01`)
    }
    if (endMonth && endYear) {
      const days = getDaysIntMonth(endYear, endMonth)
      setEnd(`${endYear}-${stringMonthEnd}-${days}`)
    }
  }

  const handleChangeTimePeriod = async (event: SelectChangeEvent | null, customPayload?: any) => {
    const period = event ? (event.target.value as CPITimePeriod) : CPITimePeriod.Custom
    handleChangeFilters(period, 'time')

    if (period === CPITimePeriod.Custom) {
      const { dateFrom, dateTo } = customPayload

      const formattedValue = formatCustomTimePickerValue(dateFrom, dateTo)
      setCustomTimePeriodValue(formattedValue)
    } else {
      setCustomTimePeriodValue('')
    }
  }

  const handleViewData = () => {
    const diff = monthDiff(new Date(start), new Date(end))
    const today = new Date()
    const endDate = new Date(end)
    const res = endDate.getTime() > today.getTime() ? today : endDate
    const resFormatted = dayjs(res).format('YYYY-MM-DD')
    handleChangeTimePeriod(null, {
      dateFrom: start,
      dateTo: resFormatted,
      stripes: diff
    })
    setAnchorEl(null)
    closeCustomPicker()
  }

  const onChangeCallback = (month: any, startMonth: any, endMonth: any) => {
    if (!startMonth && !endMonth && month) {
      setViewDataDisabled(true)
    } else if (!endMonth) {
      setViewDataDisabled(false)
    } else if (startMonth && endMonth && month) {
      setViewDataDisabled(true)
    }
  }

  // Is anything narrowing the current search? `cpiOptions` is derived from the
  // result set, so a filter that matches nothing empties it — which must not be
  // read as "this account has no CPIs activated". Without this the user lands on
  // an "activate a CPI" dead end (no toolbar, no Filters button) instead of
  // their applied filters plus a resettable empty state.
  const searchActive = filtersActive || debouncedSearchValue.length > 0

  // Are we currently rendering the "category has no CPIs to scope by" branch?
  // The nav state carried no CPIs, so the search underneath is unfiltered and
  // would list insights from other categories — suppress it. The toolbar isn't
  // mounted there, so the header's Filters button has to exit the branch on
  // click to become interactive. A category that *does* have CPIs falls through
  // to the regular empty state, which keeps its filters on screen.
  const inCategoryNavEmptyState = cameFromCategoryNav && filters.cpis.length === 0

  // Which NoIntegrations empty state, if any, replaces the list. Those branches don't
  // mount the FiltersToolbar, so the header hides its Filters button for the same
  // answer rather than leaving a dead button behind.
  const noIntegrationsVersion = resolveNoIntegrationsVersion({
    hasResults: !!data?.length,
    hasConfiguredDataSources: configuredDataSources.length > 0,
    hasCpiOptions: cpiOptions.length > 0,
    searchActive
  })

  // The page-level spinner is for having nothing to show: the first load, the unresolved v2
  // gate, or a result set going from empty to non-empty. Every other fetch runs behind the
  // list that's already on screen, which loads in place via its own skeletons.
  const showPageLoader = insightsV2 === null || initialLoading || (loading && groups.flat().length === 0)

  const handleFiltersToolbarToggle: Dispatch<SetStateAction<boolean>> = (value) => {
    if (inCategoryNavEmptyState) {
      // While the empty branch is rendering, the toolbar isn't mounted, so the
      // header's tracked `filtersToolbarOpen` is out of sync with what the user
      // sees. Force-open the toolbar on click and exit the empty branch in one
      // step — otherwise it takes two clicks (one to "close" the invisible
      // toolbar, another to actually open it).
      setCameFromCategoryNav(false)
      categoryNavCpiOptionsRef.current = null
      setFiltersToolbarOpen(true)
      return
    }
    const next = typeof value === 'function' ? (value as (prev: boolean) => boolean)(filtersToolbarOpen) : value
    setFiltersToolbarOpen(next)
  }

  return (
    <Container className="insights-container">
      <InsightsCustomHeader
        breadcrumbs={breadcrumbs.insights}
        search={search}
        setSearch={setSearch}
        filtersToolbarOpen={filtersToolbarOpen}
        setFiltersToolbarOpen={handleFiltersToolbarToggle}
        filtersActive={filtersActive}
        hasIntegrations={
          initialLoad && dataSourcesLoaded && (configuredDataSources.length > 0 || cpiOptions.length > 0)
        }
        hideFilters={!!noIntegrationsVersion}
      />

      <ContentWrapper
        line={1}
        sx={{ paddingBottom: '0 !important', position: 'relative', overflow: 'hidden !important' }}
      >
        {initialLoad && dataSourcesLoaded ? (
          <>
            {showPageLoader ? (
              // Nothing to render yet. Without this guard the conditions below briefly
              // evaluate to a NoIntegrations / NoInsightsFound branch — `cpiOptions` was
              // just cleared and `data` hasn't arrived — and the user sees a flash of
              // "You must have at least one CPI activated" before the insights resolve.
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  marginTop: `${theme.spacing.xLarge}px`
                }}
              >
                <CircularProgress size="36px" />
              </Box>
            ) : noIntegrationsVersion ? (
              <NoIntegrations version={noIntegrationsVersion === 'default' ? undefined : noIntegrationsVersion} />
            ) : inCategoryNavEmptyState ? (
              // Category-page navigation landed here with no CPIs to scope by
              // (the source category has none active), so the search running
              // underneath is unfiltered. Render the empty state without the
              // filter toolbar rather than listing insights from unrelated
              // categories under filter chips that scope nothing.
              <NoResultsWrapper>
                <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="2"
                    y="2"
                    width="124"
                    height="124"
                    rx="62"
                    stroke={getColor('stroke-primary')}
                    strokeWidth="4"
                    strokeDasharray="8 8"
                  />
                  <path
                    d="M58 82C58 83.1 58.9 84 60 84H68C69.1 84 70 83.1 70 82V80H58V82ZM64 44C56.28 44 50 50.28 50 58C50 62.76 52.38 66.94 56 69.48V74C56 75.1 56.9 76 58 76H70C71.1 76 72 75.1 72 74V69.48C75.62 66.94 78 62.76 78 58C78 50.28 71.72 44 64 44ZM69.7 66.2L68 67.4V72H60V67.4L58.3 66.2C55.6 64.32 54 61.26 54 58C54 52.48 58.48 48 64 48C69.52 48 74 52.48 74 58C74 61.26 72.4 64.32 69.7 66.2Z"
                    fill={getColor('text-secondary')}
                  />
                </svg>
                <Box className="no-results-content">
                  <Typography className="no-results-header">No Insights Found</Typography>
                  <Box>
                    <Typography className="no-results-text">There are no available insights to display.</Typography>
                    <Typography className="no-results-text">
                      Allow for a few days for new insights to be generated, or reset the page filters to see all
                      available insights.
                    </Typography>
                  </Box>
                </Box>
              </NoResultsWrapper>
            ) : (
              <>
                <FiltersToolbar
                  typeOptions={typeOptions}
                  filtersToolbarOpen={filtersToolbarOpen}
                  timeOptions={timeOptions}
                  cpiOptions={cpiOptions}
                  dataSourceOptions={dataSourceOptions}
                  severityOptions={severityOptions}
                  filters={filters}
                  setFilters={setFilters}
                  handleChangeFilters={handleChangeFilters}
                  handleResetFilters={handleResetFilters}
                  handleResetCpisFilter={handleResetCpisFilter}
                  customTimePeriodValue={customTimePeriodValue}
                  anchorEl={anchorEl}
                  closeCustomPicker={closeCustomPicker}
                  setLastChanged={setLastChanged}
                  handleCustomClick={handleCustomClick}
                  setAnchorEl={setAnchorEl}
                  handleChangeTimePeriod={handleChangeTimePeriod}
                  allCardsAreInsights={allCardsAreInsights}
                />

                <MonthRangePicker
                  onRangeSelect={onRangeSelect}
                  calendarClassName={`month-picker-container ${anchorEl && pickerOpen && 'isopen'}`}
                  headerClassName="month-picker-header"
                  monthClassName="month-picker-month"
                  columns={2}
                  initialYear={new Date().getFullYear() - 1}
                  onChangeCallback={onChangeCallback}
                />
                <ViewData className={anchorEl && pickerOpen ? 'isopen' : ''}>
                  <ThemeButton
                    themevariant="secondary"
                    sx={viewDataStyles}
                    onClick={handleViewData}
                    disabled={viewDataDisabled}
                  >
                    OK
                  </ThemeButton>
                </ViewData>

                {!loading && !data && (
                  <NoResultsWrapper>
                    <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect
                        x="2"
                        y="2"
                        width="124"
                        height="124"
                        rx="62"
                        stroke={getColor('stroke-primary')}
                        strokeWidth="4"
                        strokeDasharray="8 8"
                      />
                      <path
                        d="M58 82C58 83.1 58.9 84 60 84H68C69.1 84 70 83.1 70 82V80H58V82ZM64 44C56.28 44 50 50.28 50 58C50 62.76 52.38 66.94 56 69.48V74C56 75.1 56.9 76 58 76H70C71.1 76 72 75.1 72 74V69.48C75.62 66.94 78 62.76 78 58C78 50.28 71.72 44 64 44ZM69.7 66.2L68 67.4V72H60V67.4L58.3 66.2C55.6 64.32 54 61.26 54 58C54 52.48 58.48 48 64 48C69.52 48 74 52.48 74 58C74 61.26 72.4 64.32 69.7 66.2Z"
                        fill={getColor('text-secondary')}
                      />
                    </svg>

                    <Box className="no-results-content">
                      <Typography className="no-results-header">No Insights Found</Typography>
                      <Box>
                        <Typography className="no-results-text">There are no available insights to display.</Typography>
                        <Typography className="no-results-text">
                          Allow for a few days for new insights to be generated, or reset the page filters to see all
                          available insights.
                        </Typography>
                      </Box>
                    </Box>
                  </NoResultsWrapper>
                )}

                {groups && groups.length > 0 && selectedInsight && (
                  <Content className="content" isFiltersOpen={filtersToolbarOpen ? 'true' : 'false'}>
                    <Box className="insights-list-wrapper" sx={{ height: '100%', overflowY: 'auto' }}>
                      <InsightsList
                        data={data}
                        search={search}
                        insightsGroups={groups}
                        selectedInsight={selectedInsight}
                        handleClickInsight={handleClickInsight}
                        changeReadStatus={changeReadStatus}
                        setSelectedInsight={setSelectedInsight}
                        loading={loading}
                        handleChangeFilters={handleChangeFilters}
                        filters={filters}
                        setGroups={setGroups}
                        loadingMarking={loadingMarking}
                        setLoadingMarking={setLoadingMarking}
                      />
                    </Box>

                    <Box
                      sx={{
                        width: '1px',
                        height: '100vh',
                        background: theme.colors.strokePrimary,
                        overflow: 'hidden'
                      }}
                    />

                    <InsightErrorBoundary
                      resetKey={selectedInsight.id}
                      fallback={<InsightErrorFallback variant="main" />}
                    >
                      <MainSection
                        insight={selectedInsight}
                        getHeaderBackground={getHeaderBackground}
                        isFetching={loading}
                        loadingMarking={loadingMarking}
                        isLoading={isLoading}
                        search={search}
                      />
                    </InsightErrorBoundary>
                  </Content>
                )}
              </>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginTop: `${theme.spacing.xLarge}px`
            }}
          >
            <CircularProgress size="36px" />
          </Box>
        )}
      </ContentWrapper>
    </Container>
  )
}

export default OnyxAIInsights
